import { Link } from 'react-router-dom';

import { Button } from '@/design-system';

import { RenewSubscriptionModal } from '../components/RenewSubscriptionModal';
import { PAYMENT_PLAN_PRICING_MAP } from '../utils/constants';
import { isLowerTier } from '../utils/helpers';
import { isCancelledPlan } from '../utils/isCancelledPlan';
import { usePlanTagAttributes } from '../utils/paymentPlanCard.utils';
import { useHasAnyApprovals } from './hooks/useShowApprovalPlanCard';
import { useBillingPlanStore } from './store';
import { useSetUpBillingHandlers } from './store/helpers';
import { PaymentPlanCardTiers } from './types';
import { isTierNeedingCard } from './utils/isTierNeedingCard';

export const PaymentPlanCardActionButton = ({
  cardPlanType,
}: {
  cardPlanType: PaymentPlanCardTiers;
}) => {
  const {
    setOpen,
    setError,
    approvals,
    changePlanTrigger,
    changePlanIsMutating,
    hasPaymentMethod,
    upcomingPlan,
    currentPlan,
    isPersonal,
  } = usePlanTagAttributes();

  const hasAnyApprovals = useHasAnyApprovals();

  const setTargetPlanTier = useBillingPlanStore((s) => s.setTargetPlanTier);
  const setIsWelcomeMessageModalOpen = useBillingPlanStore(
    (s) => s.setIsWelcomeMessageModalOpen
  );

  const {
    handleSubscribeToPlus,
    handleFreeSetupBilling,
    handleStartupAndPremierEnrollment,
    handleUpdatePaymentMethod,
    isMutating,
  } = useSetUpBillingHandlers();

  const currentTier = currentPlan?.tier;
  const upcomingTier = upcomingPlan?.tier;

  const hasCancelled = isCancelledPlan(currentPlan, upcomingPlan);

  if (cardPlanType === currentTier) {
    if (hasCancelled && currentTier !== 'partner' && !upcomingTier) {
      return (
        <RenewSubscriptionModal
          planType={cardPlanType}
          price={PAYMENT_PLAN_PRICING_MAP[cardPlanType].pricePerSeat}
          maxSeats={PAYMENT_PLAN_PRICING_MAP[cardPlanType].maxSeats}
          disabled={!hasPaymentMethod}
          changeToTier={cardPlanType}
          onSubscribeSuccess={() => setOpen(cardPlanType)(true)}
        />
      );
    }
    const tierNeedsCard = isTierNeedingCard(currentTier);
    if (tierNeedsCard) {
      if (!hasPaymentMethod) {
        return (
          <Button
            size="sm"
            variant="normal"
            color="primary"
            loading={isMutating}
            onClick={() => {
              handleUpdatePaymentMethod();
            }}
            className="w-full"
          >
            Add Card
          </Button>
        );
      }
      return (
        <Link to="?tab=2">
          <Button size="sm" color="primary" className="w-full">
            Manage usage limits
          </Button>
        </Link>
      );
    }
    return null;
  } else {
    switch (cardPlanType) {
      case 'developer': {
        if (currentTier === 'free' && handleFreeSetupBilling) {
          return (
            <Button
              size="sm"
              color="primary"
              variant={hasAnyApprovals ? 'outlined' : 'normal'}
              loading={isMutating}
              onClick={handleFreeSetupBilling}
              className="w-full"
            >
              Add card to remove trace limit
            </Button>
          );
        }
        return null;
      }
      case 'plus': {
        const isLower = isLowerTier(currentPlan?.tier, 'plus');
        if (upcomingTier === 'plus') return null;
        return (
          <Button
            onClick={handleSubscribeToPlus}
            loading={isMutating}
            color={isLower ? 'primary' : 'secondary'}
            size="sm"
            // personal plan will have main cta to manage usage limits so make it outlined
            variant={isPersonal ? 'outlined' : 'normal'}
            className="w-full"
          >
            {`${isLower ? 'Upgrade to' : 'Subscribe to'} Plus`}
          </Button>
        );
      }
      case 'applyEnterprise': {
        return (
          <a
            href="https://www.langchain.com/contact-sales"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full "
          >
            <Button className="w-full" variant="outlined">
              Contact Sales
            </Button>
          </a>
        );
      }
      case 'startup':
      case 'premier': {
        const isApproved = approvals[cardPlanType];
        if (!isApproved || upcomingTier === cardPlanType) return null;
        return (
          <Button
            onClick={async () => {
              if (!hasPaymentMethod) {
                handleStartupAndPremierEnrollment(cardPlanType);
                return;
              }
              try {
                await changePlanTrigger({
                  json: {
                    tier: cardPlanType as
                      | 'startup'
                      | 'startup_v0'
                      | 'partner'
                      | 'premier',
                  },
                });
                setTargetPlanTier(cardPlanType);
                setIsWelcomeMessageModalOpen(true);

                // todo: when removing usePlanTagAttributes, can just remove this setOpen call
                setOpen(cardPlanType)(true);
              } catch (e) {
                if (e instanceof Error && e.message) {
                  setError(e.message);
                } else {
                  setError('Something went wrong.');
                }
              }
            }}
            loading={!hasPaymentMethod ? isMutating : changePlanIsMutating}
            size="sm"
            color="primary"
            className="w-full"
          >
            Activate
          </Button>
        );
      }
      case 'partner': {
        return null;
      }
    }
  }
  return null;
};
